import { useEffect } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { ArrowLeft, FileEdit, Loader2, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { AdjustmentCard } from "@/components/payroll/AdjustmentCard";
import { DianReadinessBanner } from "@/components/companies/DianReadinessBanner";
import { usePayrollAdjustments } from "@/lib/api/payroll-adjustments.queries";
import { useActiveCompany } from "@/lib/company-context";

export const Route = createFileRoute("/companies/$companyId/nomina/ajustes/")({
  component: AdjustmentsIndexPage,
});

function AdjustmentsIndexPage() {
  return (
    <RequireAuth>
      <AppShell>
        <AdjustmentsIndexContent />
      </AppShell>
    </RequireAuth>
  );
}

function AdjustmentsIndexContent() {
  const { t } = useTranslation();
  const { companyId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const adjustmentsQ = usePayrollAdjustments(companyId, { pageSize: 50 });

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6">
      <DianReadinessBanner companyId={companyId} documentKind="nomina_ajuste" />
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link
            to="/companies/$companyId/nomina"
            params={{ companyId }}
            className="inline-flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3 w-3" />
            {t("payroll.adjustment.list.back")}
          </Link>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight">
            {t("payroll.adjustment.list.title")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("payroll.adjustment.list.subtitle")}
          </p>
        </div>
        <Button asChild>
          <Link
            to="/companies/$companyId/nomina/ajustes/new"
            params={{ companyId }}
          >
            <Plus className="h-4 w-4" />
            {t("payroll.adjustment.list.new")}
          </Link>
        </Button>
      </div>

      {adjustmentsQ.isPending ? (
        <div className="flex items-center justify-center rounded-lg border bg-card py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : adjustmentsQ.isError ? (
        <ErrorState
          title={t("payroll.adjustment.list.error_title")}
          description={t("payroll.adjustment.list.error_body")}
          onRetry={() => void adjustmentsQ.refetch()}
          retryLabel={t("common.retry")}
        />
      ) : (adjustmentsQ.data?.adjustments.length ?? 0) === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center gap-4 py-16 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
              <FileEdit className="h-8 w-8 text-primary" />
            </div>
            <div className="max-w-sm space-y-1">
              <h2 className="text-lg font-semibold">
                {t("payroll.adjustment.list.empty_title")}
              </h2>
              <p className="text-sm text-muted-foreground">
                {t("payroll.adjustment.list.empty_body")}
              </p>
            </div>
            <Button asChild>
              <Link
                to="/companies/$companyId/nomina/ajustes/new"
                params={{ companyId }}
              >
                <Plus className="h-4 w-4" />
                {t("payroll.adjustment.list.new")}
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {adjustmentsQ.data!.adjustments.map((a) => (
            <AdjustmentCard key={a.id} companyId={companyId} adjustment={a} />
          ))}
        </div>
      )}
    </div>
  );
}
